import { Context } from '../context/Context';
import { Gltf, GltfParser, ResolvedPrimitive } from '../gltf';
import { GltfMesh } from '../mesh';
import { RenderObject } from '../object/RenderObject';
import { ArrayTexture } from '../texture';
import { TerrainMaterial, TERRAIN_SLOT_COUNT } from '../material';
import { vec3 } from 'gl-matrix';
import { Scene } from './Scene';
import { TerrainRef } from './SceneManifest';
import { TiledTerrainStreamer, loadTerrainSharedMaterial } from './TiledTerrainStreamer';
import { TileMeshClient, TileMeshClientConfig, WorkerLike } from './terrain/TileMeshClient';
import { inflateGzip } from '../util/gzip';

type TerrainSlotExtras = {
  id?: string;
  name?: string;
  color?: number[];
  tilingScale?: number;
  albedoImage?: number | null;
  normalImage?: number | null;
  roughnessImage?: number | null;
};

type DecodedImage = { data: Uint8Array; width: number; height: number };

/**
 * Build a TerrainMaterial by decoding surface PNGs embedded in a terrain GLB and
 * assembling them into 4-layer TEXTURE_2D_ARRAYs. Slots with no albedo image fall back
 * to a 1×1 solid-color texture using the slot's color (gray for unassigned slots).
 * Returns undefined only when the extras contain no slot definitions at all.
 */
export async function buildTerrainMaterial(
  ctx: Context,
  json: Gltf,
  binaries: Uint8Array[],
  extras: Record<string, any>,
): Promise<TerrainMaterial | undefined> {
  const slots = (extras['surfaces'] as TerrainSlotExtras[] | undefined) ?? [];
  if (slots.length === 0) return undefined;

  const anyNormal = slots.some((s) => typeof s.normalImage === 'number');
  const anyRoughness = slots.some((s) => typeof s.roughnessImage === 'number');

  const slotColors: [number, number, number][] = [];
  const slotTiling: number[] = [];
  const slotHasAlbedo: boolean[] = [];

  const albedoDecoded: (DecodedImage | undefined)[] = [];
  const normalDecoded: (DecodedImage | undefined)[] = [];
  const roughnessDecoded: (DecodedImage | undefined)[] = [];

  for (let i = 0; i < TERRAIN_SLOT_COUNT; i++) {
    const slot = slots[i];
    const color: [number, number, number] = slot?.color
      ? [slot.color[0] ?? 0.5, slot.color[1] ?? 0.5, slot.color[2] ?? 0.5]
      : [0.5, 0.5, 0.5];
    slotColors.push(color);
    slotTiling.push(slot?.tilingScale ?? 1);

    const albedo = await decodeSlotImage(ctx, json, binaries, slot?.albedoImage);
    albedoDecoded.push(albedo);
    slotHasAlbedo.push(!!albedo);

    normalDecoded.push(
      anyNormal ? await decodeSlotImage(ctx, json, binaries, slot?.normalImage) : undefined,
    );
    roughnessDecoded.push(
      anyRoughness ? await decodeSlotImage(ctx, json, binaries, slot?.roughnessImage) : undefined,
    );
  }

  const firstDecoded =
    albedoDecoded.find((d) => d) ||
    normalDecoded.find((d) => d) ||
    roughnessDecoded.find((d) => d);
  const width = firstDecoded?.width ?? 1;
  const height = firstDecoded?.height ?? 1;

  const albedoLayers = albedoDecoded.map(
    (d, i) => d?.data ?? solidLayer(slotColors[i], 255, width, height),
  );
  const normalLayers = anyNormal
    ? normalDecoded.map((d) => d?.data ?? solidLayer([0.5, 0.5, 1.0], 255, width, height))
    : [];
  const roughnessLayers = anyRoughness
    ? roughnessDecoded.map((d) => d?.data ?? solidLayer([0.8, 0.8, 0.8], 255, width, height))
    : [];

  const albedoArray = ArrayTexture.fromLayers(ctx, albedoLayers, width, height, {
    srgb: true,
    mipmaps: true,
  });
  const material = new TerrainMaterial(albedoArray);
  material.slotColors = slotColors.map((c) => Float32Array.from(c) as unknown as vec3);
  material.slotTiling = slotTiling;
  material.slotHasAlbedo = slotHasAlbedo;

  if (anyNormal) {
    material.normalArray = ArrayTexture.fromLayers(ctx, normalLayers, width, height, {
      srgb: false,
      mipmaps: true,
    });
  }
  if (anyRoughness) {
    material.roughnessArray = ArrayTexture.fromLayers(ctx, roughnessLayers, width, height, {
      srgb: false,
      mipmaps: true,
    });
  }

  return material;
}

async function decodeSlotImage(
  ctx: Context,
  json: Gltf,
  binaries: Uint8Array[],
  imageIndex: number | null | undefined,
): Promise<DecodedImage | undefined> {
  if (typeof imageIndex !== 'number') return undefined;
  const raw = GltfParser.extractImageByIndex(json, binaries, imageIndex);
  if (!raw) return undefined;
  return ctx.resourceLoader.loadImageData(raw.imageData, raw.mimeType);
}

function solidLayer(
  color: [number, number, number],
  alpha: number,
  width: number,
  height: number,
): Uint8Array {
  const r = Math.round(color[0] * 255);
  const g = Math.round(color[1] * 255);
  const b = Math.round(color[2] * 255);
  const buf = new Uint8Array(width * height * 4);
  for (let i = 0; i < buf.length; i += 4) {
    buf[i] = r;
    buf[i + 1] = g;
    buf[i + 2] = b;
    buf[i + 3] = alpha;
  }
  return buf;
}

export interface StandaloneTerrain {
  object: RenderObject;
  /** The resolved primitive used to build the mesh. Editors can build a
   * MeshRaycaster from this for vertical height queries. */
  primitive: ResolvedPrimitive;
}

/**
 * Parse a standalone terrain GLB (as produced by the backend `/terrain-preview`
 * endpoint) and return a ready-to-render RenderObject whose mesh is the terrain
 * primitive and whose material is a TerrainMaterial sampling the embedded
 * surface PNGs.
 *
 * Returns `undefined` when the GLB contains no primitive tagged with `_TERRAIN`.
 */
export async function loadStandaloneTerrain(
  scene: Scene,
  glbBytes: Uint8Array,
): Promise<StandaloneTerrain | undefined> {
  const parser = new GltfParser(glbBytes);
  const { json, binaries } = parser.parse();
  const meshes = parser.resolveAllMeshes(json, binaries);

  for (const mesh of meshes) {
    for (let i = 0; i < mesh.primitives.length; i++) {
      const prim = mesh.primitives[i];
      if (!prim.extras?.['_TERRAIN']) continue;

      const gltfMesh = new GltfMesh(`_terrain_${mesh.name}_${i}`, scene.ctx, scene, prim);
      const obj = scene.objectFactory.instantiateMesh(gltfMesh);
      const material = await buildTerrainMaterial(scene.ctx, json, binaries, prim.extras);
      if (material) obj.material = material;
      return { object: obj, primitive: prim };
    }
  }

  return undefined;
}

/**
 * Load a tiled terrain from the manifest's `terrain` block. Fetches the shared
 * surfaces GLB + heightmap PNG once, spins up a {@link TileMeshClient} Web Worker
 * fed by those inputs, and blocks on the 3×3 ring around (spawnX, spawnZ) before
 * returning. The returned streamer must be ticked from {@link Scene}'s per-frame
 * hook so the ring follows the camera.
 */
export async function loadTiledTerrain(
  scene: Scene,
  ref: TerrainRef,
  spawnX: number,
  spawnZ: number,
  terrainGenWorker?: WorkerLike,
): Promise<TiledTerrainStreamer> {
  let sharedBytes: Uint8Array;
  try {
    const compressed = await scene.ctx.resourceLoader.loadResource(ref.sharedGlbPath + '.gz');
    sharedBytes = await inflateGzip(compressed);
  } catch {
    sharedBytes = await scene.ctx.resourceLoader.loadResource(ref.sharedGlbPath);
  }
  const sharedMaterialPromise = loadTerrainSharedMaterial(scene, sharedBytes);

  let heightmapGrid: { data: Uint8Array; width: number; height: number } | null = null;
  if (ref.heightmapPath) {
    try {
      const bytes = await scene.ctx.resourceLoader.loadResource(ref.heightmapPath);
      heightmapGrid = await scene.ctx.resourceLoader.loadImageData(bytes, 'image/png');
    } catch (e) {
      console.warn('Failed to load terrain heightmap PNG:', e);
    }
  }

  // Fall back to the legacy "centered terrain" assumption when the manifest predates
  // explicit width/depth fields (worldMin == -half ⇒ width = -2 * worldMin).
  const worldWidth = ref.worldWidth ?? -2 * ref.worldMinX;
  const worldDepth = ref.worldDepth ?? -2 * ref.worldMinZ;
  const heightmapCellSize = ref.heightmapCellSize ?? 0;
  const heightmapAmplitude = ref.heightmapAmplitude ?? 0;
  const seed = ref.seed ?? 0;
  const surfaceRules = ref.surfaceRules ?? [];

  // Resolution = max(width, depth) / cellSize. Defaults track TerrainBuilder.MIN_RESOLUTION.
  const resolution =
    heightmapCellSize > 0 ? Math.max(worldWidth, worldDepth) / heightmapCellSize : 64;

  const meshClient = new TileMeshClient();
  const clientInit: TileMeshClientConfig = {
    width: worldWidth,
    depth: worldDepth,
    resolution,
    seed: String(seed),
    surfaceRules,
    source: heightmapGrid
      ? {
          kind: 'heightmap',
          pixels: heightmapGrid.data,
          width: heightmapGrid.width,
          height: heightmapGrid.height,
          amplitude: heightmapAmplitude,
          worldMinX: ref.worldMinX,
          worldMinZ: ref.worldMinZ,
          worldWidth,
          worldDepth,
        }
      : {
          kind: 'noise',
          octaves: ref.noiseOctaves ?? 3,
          baseFrequency: ref.noiseBaseFrequency ?? 1,
          amplitude: heightmapAmplitude || 4,
          falloffShape: ref.noiseFalloffShape ?? 'none',
          worldWidth,
          worldDepth,
        },
  };
  meshClient.init(clientInit);
  if (terrainGenWorker) {
    meshClient.useWorker(terrainGenWorker);
  }

  const streamer = new TiledTerrainStreamer(
    scene,
    meshClient,
    ref.tileWorldSize,
    ref.worldMinX,
    ref.worldMinZ,
    worldWidth,
    worldDepth,
    ref.tilesX,
    ref.tilesZ,
    ref.maxLodLevel ?? 0,
    sharedMaterialPromise,
    heightmapGrid,
    heightmapAmplitude,
    heightmapCellSize,
  );
  await streamer.loadInitialTiles(spawnX, spawnZ);
  return streamer;
}
